/**
 * useRevenueAccounting
 *
 * Fetches the full order accounting payload via the existing
 * useGetOrderAccounting hook and exposes only revenue-kind entries,
 * grouped into the 23-code presentation model.
 *
 * Design decisions:
 *  - AccountingFilter does not formally support kind:"revenue", so we do
 *    NOT pass a filter to the GraphQL query. Filtering is performed
 *    client-side via filterRevenueEntries() to satisfy acceptance criteria
 *    and remain resilient to backend schema changes.
 *  - Amount resolution follows: amount_override (non-zero) → amount_calculated
 *    → null (unavailable). The `amount` field is never used as fallback.
 *  - No mutations, no aggregation requests, no backend changes.
 */

import { useMemo } from 'react';
import { useGetOrderAccounting } from 'hooks/useGetOrderAccounting';
import {
  buildKPIs,
  filterRevenueEntries,
  groupRevenueEntries,
  resolveDebtEquityContext,
} from '../revenue-model';
import { UseRevenueAccountingReturn } from '../types';

/**
 * Hook that fetches accounting data for an order and returns the
 * revenue-only presentation model.
 *
 * @param orderId - The order _id string (from useParams or parent prop)
 */
const useRevenueAccounting = (orderId: string): UseRevenueAccountingReturn => {
  const { data, isLoading, isError, refetch: queryRefetch } = useGetOrderAccounting({
    orderId,
    // No AccountingFilter for kind:"revenue" — unsupported by backend schema.
    // Full payload is fetched and filtered client-side below.
    queryKey: 'revenue-accounting',
    queryConfig: {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
    },
  });

  const revenueEntries = useMemo(
    () => filterRevenueEntries(data?.getOrder?.accounting ?? []),
    [data]
  );

  const grouped = useMemo(
    () => groupRevenueEntries(revenueEntries),
    [revenueEntries]
  );

  const kpis = useMemo(
    () => buildKPIs(grouped),
    [grouped]
  );

  const debtEquityContext = useMemo(
    () => resolveDebtEquityContext(grouped),
    [grouped]
  );

  const hasOrder = !isLoading && !isError && data?.getOrder != null;

  const refetch = () => { queryRefetch(); };

  return {
    isLoading,
    isError,
    revenueEntries,
    grouped,
    kpis,
    debtEquityContext,
    hasOrder,
    refetch,
  };
};

export default useRevenueAccounting;
